import React from 'react'
import { Button } from 'primereact/button'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { bindActionCreators } from 'redux'
import moment from 'moment'
import styled from 'styled-components'
import i18next from 'i18next'

// local imports
import { actionCreators } from 'state/state-index'
import { PlanTypes } from 'types/BaseType'
import { ICalendarPlan } from 'types/CalendarType'
import { ICalendarSquads, IEmployeeSummary } from 'types/Employee'

interface ICalendarFooterProps {
  employee: React.RefObject<IEmployeeSummary | null>
  selectedEmployee: IEmployeeSummary
  month: moment.Moment
  squadsEvents: ICalendarSquads[] | undefined
  showSquadsEvent: boolean
  events: ICalendarPlan[]
  handleMultipleSelectedDay: () => void
  daysState: any
}

export default function CalendarFooter(props: ICalendarFooterProps) {
  const { t } = useTranslation()
  const {
    month,
    squadsEvents,
    showSquadsEvent,
    events,
    daysState,
    handleMultipleSelectedDay,
    employee,
    selectedEmployee,
  } = props

  const dispatch = useDispatch()
  const { changeAllDayState } = bindActionCreators(actionCreators, dispatch)

  /**
   * calculate home from work and office work counts.
   */
  const calculatedBottomSentence = () => {
    let squadsEvent: ICalendarPlan[] = []
    if (squadsEvents) {
      squadsEvents.map((event) => {
        // @ts-ignore
        squadsEvent.push(...event.Events)
        return event
      })
    }

    let officeWorkCounts = 0
    let homeWorkCounts = 0
    let commonWorkCounts = 0
    let firstDay = month.clone().startOf('month')
    let lastDay = month.clone().endOf('month')

    if (showSquadsEvent && squadsEvent) {
      squadsEvent.forEach((f) => {
        let date = moment(f.reservationDate)
        if (date >= firstDay && date <= lastDay) {
          if (f.planType === PlanTypes.HomeOffice) homeWorkCounts++
          else if (f.planType === PlanTypes.TableReservation || f.planType === PlanTypes.TableFloorReservation) {
            officeWorkCounts++
          } else if (
            f.planType === PlanTypes.OtherLocationTableReservation ||
            f.planType === PlanTypes.OtherLocationTableFloorReservation
          ) {
            commonWorkCounts++
          }
        }
      })
    } else {
      events?.forEach((f) => {
        let date = moment(f.reservationDate)
        if (date >= firstDay && date <= lastDay) {
          if (f.planType === PlanTypes.HomeOffice) homeWorkCounts++
          else if (f.planType === PlanTypes.TableReservation || f.planType === PlanTypes.TableFloorReservation) {
            officeWorkCounts++
          } else if (
            f.planType === PlanTypes.OtherLocationTableReservation ||
            f.planType === PlanTypes.OtherLocationTableFloorReservation
          ) {
            commonWorkCounts++
          }
        }
      })
    }

    return (
      <div className="of-calendar-bottom-info">
        <div className="of-calendar-bottom-text">
          "{month.locale(i18next.language).format('MMMM')}" {t('date-terms.in-month')}{' '}
          <strong>{officeWorkCounts}</strong> {t('date-terms.day')} {t('calendar.work-from-my-firm')} /{' '}
          <strong>{commonWorkCounts}</strong> {t('date-terms.day')} {t('calendar.work-from-shared-offices')} /{' '}
          <strong>{homeWorkCounts}</strong> {t('date-terms.day')} {t('tabs.remote-work')} - {t('most-used-words.total')}{' '}
          <strong>{officeWorkCounts + homeWorkCounts + commonWorkCounts}</strong> {t('most-used-words.reservation')} /{' '}
          {t('most-used-words.notification')}
        </div>
      </div>
    )
  }

  const handleMultipleSideBarEvent = () => handleMultipleSelectedDay?.()

  const getAnyCheckedDay = () => {
    const filtered = daysState.filter((x: any) => x.day.isChecked)
    return filtered.length > 0
  }

  const getAllCheckedDay = () => {
    const visibleLength = daysState.filter((x: any) => x.day.isVisible)?.length
    const checkedLength = daysState.filter((x: any) => x.day.isChecked)?.length
    return visibleLength === checkedLength
  }

  const chooseBtnText = () => {
    const eventList = daysState
      .filter((x: any) => x.day.isVisible && x.day.isChecked)
      .map((e: any) => !!Object.keys(e.event).length)
    const btnText = eventList.includes(true) || !getAnyCheckedDay() ? t('buttons.go-detail') : t('buttons.notify')
    return btnText
  }

  const setAllCheckBoxStatus = () => changeAllDayState(true)
  const clearAllCheckBoxStatus = () => changeAllDayState(false)
  const isButtonDisable = getAnyCheckedDay() ? false : true
  const selectAllBtnDisable = getAllCheckedDay() ? true : false

  return (
    <div className="d-flex flex-column flex-xxl-row align-items-center mt-4 gap-4">
      <div>{calculatedBottomSentence()}</div>

      {employee.current !== null && employee.current.id !== selectedEmployee?.id ? null : (
        <div className="gap-3 d-flex align-items-center">
          <div className="d-flex gap-1">
            <TextButton
              id="chooseAllCalendarDays"
              disabled={selectAllBtnDisable}
              className="p-button-text p-button-plain"
              onClick={setAllCheckBoxStatus}>
              {t('buttons.choose-all')}
            </TextButton>

            <TextButton
              id="clearCalendarDays"
              width={85}
              className="p-button-text p-button-plain"
              onClick={clearAllCheckBoxStatus}
              disabled={isButtonDisable}>
              {t('buttons.clear')}
            </TextButton>
          </div>
          <CustomButton id="goToDaysDetail" onClick={() => handleMultipleSideBarEvent()} disabled={isButtonDisable}>
            {chooseBtnText()}
          </CustomButton>
        </div>
      )}
    </div>
  )
}

const TextButton = styled(Button)`
  width: ${({ width }: { width?: number }) => `${width || 120}px`};
  height: 50px;
  justify-content: center;
  color: #363636;
  text-decoration: underline;
  border-radius: 10px;
  color: #363636 !important;

  :focus {
    outline: 0 none;
    outline-offset: 0;
    box-shadow: none;
    background-color: none;
  }

  :disabled {
    opacity: 0.4;
  }
`

const CustomButton = styled(Button)`
  min-width: 200px;
  height: 50px;
  justify-content: center;
  background: #45bfe5;
  border-radius: 10px;
`
